/**
 * Tracking-platform toggles (admin-controlled, localStorage-backed).
 *
 * Each tracking init point reads `isTrackingEnabled(<key>)` and no-ops
 * when disabled. Toggles persist in localStorage so the choice survives
 * a hard reload — which is essential because some libraries
 * (Sentry, Vercel Speed Insights, the inline gtag block in index.html)
 * initialize before React mounts and have no in-React unmount path.
 *
 * Why localStorage and not a context: Sentry's `instrument.ts` is
 * imported as the very first line of `main.tsx` (before React). A React
 * context is unreachable that early. localStorage is sync, available in
 * the global scope, and survives the SW cache.
 *
 * Toggle UX (in /admin/tracking):
 *   - Toggle off → sets `<key>:disabled` in storage
 *   - Reload prompt → user clicks reload, the page boots with the gate
 *     active and that platform never initializes for the session
 *   - Toggle on → removes the disabled flag (default is enabled)
 *
 * The default-enabled bias is deliberate: a missing/unparseable storage
 * key behaves the same as fresh-install (every platform on). Operators
 * who want a clean Lighthouse run flip everything off, reload, run the
 * audit, then flip back on.
 */

export interface TrackingToggle {
  /** Stable id used as the storage key suffix. Never rename — old
   *  browser sessions keep their setting under the old id. */
  key: string;
  label: string;
  /** What it does, plain English. Shown next to the switch. */
  description: string;
  /** Where it lives in the codebase, for the admin tooltip. */
  source: string;
  /** Set to true if disabling REQUIRES a hard reload to fully unload
   *  (e.g. Sentry, gtag, ManagedScripts). The UI shows a reload prompt
   *  on toggle when true. The conservative default is `true` — most
   *  platforms in this app do require a reload because their gate is
   *  read at module-init or inside JSX that doesn't re-evaluate without
   *  a re-render of <App />. Only mark `false` when the gate is
   *  genuinely re-checked on every effect run. RouteTracker and
   *  AnalyticsInit qualify because their useEffect bodies re-read the
   *  gate. ErrorLoggerInit's gate runs once on mount, so it also
   *  qualifies (subsequent toggles wait for the next cold load — same
   *  practical effect as reload). Conditional-render toggles like
   *  vercel-analytics and managed-scripts do require reload. */
  requiresReload: boolean;
  /** Set to true when the toggle ALSO needs to persist to the server-side
   *  `public.tracking_config` table so Edge Functions + Vercel Functions
   *  can read it. Only `sentry` qualifies right now — server modules
   *  query `config_key='sentry'` before forwarding events. Future
   *  server-side toggles (e.g. server-side analytics if we ever add
   *  one) would also set this true. */
  serverPersisted?: boolean;
}

export const TRACKING_TOGGLES: readonly TrackingToggle[] = [
  {
    key: 'sentry',
    label: 'Sentry',
    description: 'Error monitoring + performance tracing (client + server).',
    source: 'src/instrument.ts + api/_shared/sentry.ts + supabase/functions/_shared/sentry.ts',
    requiresReload: true,
    serverPersisted: true,
  },
  {
    key: 'first-party-analytics',
    label: 'First-party analytics',
    description: 'Custom analytics.ts beacons (page_view, vibe_*, conversions).',
    source: 'src/lib/analytics.ts (via <AnalyticsInit />)',
    requiresReload: false,
  },
  {
    key: 'route-tracker',
    label: 'SPA route tracker',
    description: 'Fires page_view on SPA route changes (no SSR - needed in addition to the boot page_view).',
    source: '<RouteTracker /> in App.tsx',
    requiresReload: false,
  },
  {
    key: 'vercel-analytics',
    label: 'Vercel Analytics',
    description: '@vercel/analytics - page-view + custom events to vercel.com/analytics.',
    source: '<Analytics /> in App.tsx',
    // The gate is read inside JSX: {isTrackingEnabled('vercel-analytics') && <Analytics />}
    // which only re-evaluates when <App /> re-renders — and <App /> is
    // mostly static. Treat as reload-required for honest UX.
    requiresReload: true,
  },
  {
    key: 'vercel-speed-insights',
    label: 'Vercel Speed Insights',
    description: '@vercel/speed-insights - Real-User-Monitoring of Core Web Vitals.',
    source: '<SpeedInsights /> in App.tsx',
    requiresReload: true,
  },
  {
    key: 'managed-scripts',
    label: 'Managed scripts (DB-driven pixels)',
    description: 'Third-party tracking pixels mounted from /admin/scripts (GA4, Meta Pixel, UET, etc.).',
    source: '<ManagedScripts /> + /api/managed-scripts',
    requiresReload: true,
  },
  {
    key: 'error-logger',
    label: 'Custom error logger',
    description: 'Pre-Sentry homegrown error log shipper. Kept for backfill / Sentry overlap audit.',
    source: 'src/lib/errorLogger.ts (via <ErrorLoggerInit />)',
    requiresReload: false,
  },
  {
    key: 'gtag-default-consent',
    label: 'gtag default-consent block',
    description: 'Inline <script> in index.html that primes window.dataLayer + sets default consent. Disabling skips the inline init (downstream gtag.js loads from ManagedScripts will still work but consent default is "denied" everywhere).',
    // Inline script reads
    //   localStorage.getItem('spotivibly:tracking:gtag-default-consent') === 'disabled'
    // at parse time (no window flag involved).
    source: 'index.html - gated via localStorage spotivibly:tracking:gtag-default-consent',
    requiresReload: true,
  },
] as const;

const STORAGE_PREFIX = 'spotivibly:tracking:';

/**
 * Returns true if the platform is enabled (the default). Returns false
 * only when the admin has explicitly flipped it off.
 *
 * Safe to call before React mounts — no DOM dependency beyond
 * `window.localStorage` (which is available throughout the page lifecycle
 * after the first script tag executes).
 */
export function isTrackingEnabled(key: string): boolean {
  // SSR safety net even though Vite builds an SPA — keep the check so
  // the helper is reusable in framework-agnostic init code.
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + key) !== 'disabled';
  } catch {
    // localStorage can throw in private mode / quota / SecurityError.
    // Fail open (enabled) — same behavior as the un-toggled default.
    return true;
  }
}

export function setTrackingEnabled(key: string, on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (on) {
      window.localStorage.removeItem(STORAGE_PREFIX + key);
    } else {
      window.localStorage.setItem(STORAGE_PREFIX + key, 'disabled');
    }
  } catch {
    // Best-effort; quota/private-mode failures are non-fatal.
  }
}

/** Convenience: snapshot every key's current state. */
export function getAllTrackingStates(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const t of TRACKING_TOGGLES) out[t.key] = isTrackingEnabled(t.key);
  return out;
}

/**
 * Disable / enable EVERYTHING in one shot. Used by the "all off / all on"
 * buttons in the admin UI (clean-Lighthouse-run workflow).
 */
export function setAllTracking(on: boolean): void {
  for (const t of TRACKING_TOGGLES) setTrackingEnabled(t.key, on);
}
